import { useCallback, useEffect, useRef } from 'react';

const useCarfaxScrollDetection = (isEnabled) => {
    const hasUserScrolledRef = useRef(false);
    const registeredTilesRef = useRef(new Set());
    const initialViewportTilesRef = useRef(new Set());

    const handleScroll = useCallback(() => {
        const carfaxTiles = document?.querySelectorAll('.carfax-snapshot-hover');

        if (!hasUserScrolledRef.current) {
            hasUserScrolledRef.current = true;

            // On first scroll, capture tiles already in viewport and ignore them
            carfaxTiles.forEach((tile) => {
                const tilePositioning = tile.getBoundingClientRect();
                const isVisible = tilePositioning.top >= 0 && tilePositioning.top <= window.innerHeight;
                if (isVisible) {
                    const tileSnapshotKey = tile.dataset.snapshotkey;
                    initialViewportTilesRef.current.add(tileSnapshotKey);
                }
            });

            return; // Skip processing on first scroll
        }

        // Find the first visible tile that hasn't been processed AND wasn't initially visible on page load
        const scrolledCarfaxTile = Array.from(carfaxTiles).find((tile) => {
            const scrolledTilePositioning = tile.getBoundingClientRect();
            const isScrolledTileVisible = scrolledTilePositioning.top >= 0 && scrolledTilePositioning.top <= window.innerHeight;
            const scrolledTileSnapshotKey = tile.dataset.snapshotkey;
            // Find me a Carfax tile that is currently visible AND hasn't been registered AND wasn't visible when the page first loaded
            return isScrolledTileVisible && !registeredTilesRef.current.has(scrolledTileSnapshotKey) && !initialViewportTilesRef.current.has(scrolledTileSnapshotKey);
        });

        if (scrolledCarfaxTile) {
            const refreshSnapshotEvent = new Event('refreshCarfaxSnapshotData');
            document?.dispatchEvent(refreshSnapshotEvent);

            // Mark this tile as processed
            const processedTileSnapshotKey = scrolledCarfaxTile.dataset.snapshotkey;
            registeredTilesRef.current.add(processedTileSnapshotKey);
        }
    }, []);

    useEffect(() => {
        if (!isEnabled) return;

        // eslint-disable-next-line github/prefer-observers
        window.addEventListener('scroll', handleScroll);

        // eslint-disable-next-line consistent-return
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [isEnabled, handleScroll]);

    // Cleanup function to reset refs if needed
    const resetScrollDetection = useCallback(() => {
        hasUserScrolledRef.current = false;
        registeredTilesRef.current.clear();
        initialViewportTilesRef.current.clear();
    }, []);

    return { resetScrollDetection };
};

export default useCarfaxScrollDetection;
